/**
 * API 주소·서비스명은 Railway 등 배포 환경에서 VITE_API_BASE_URL, VITE_SERVICE_NAME 으로 설정.
 * origin만 넣어도 됨 (예: https://inside-api-production.up.railway.app) → 코드에서 /api 붙임.
 */

// ----- 상수 -----
const DEFAULT_BASE = 'http://localhost:8080/api'
const API_PREFIX = '/api'
const DEFAULT_ORIGIN = 'http://localhost:8080'
const DEFAULT_SERVICE_NAME = 'dreamity'
const DEFAULT_APPLE_CLIENT_ID = 'com.torieflab.dreamityplanner.web'

// ----- env (상단에서 한 번만 읽기) -----
const rawApiBase = import.meta.env.VITE_API_BASE_URL as string | undefined
const rawServiceName = import.meta.env.VITE_SERVICE_NAME as string | undefined
const rawAppleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined
const rawAppleCallbackUri = import.meta.env.VITE_APPLE_CALLBACK_URI as string | undefined

// ----- 유틸 -----
function normalizeApiBaseUrl(raw: string | undefined): string {
  if (!raw || !raw.trim()) return DEFAULT_BASE
  const origin = raw.replace(/\/+$/, '')
  if (origin.endsWith(API_PREFIX)) return origin
  return `${origin}${API_PREFIX}`
}

function parseOriginFromBase(base: string): string {
  if (base.startsWith('http://') || base.startsWith('https://')) {
    try {
      return new URL(base).origin
    } catch {
      return DEFAULT_ORIGIN
    }
  }
  return DEFAULT_ORIGIN
}

// ----- 파생 값 (상단에서 계산) -----
const apiBaseUrl = import.meta.env.DEV
  ? '/api'
  : normalizeApiBaseUrl(rawApiBase || DEFAULT_ORIGIN)
const apiHost = parseOriginFromBase(apiBaseUrl)
const serviceName = (rawServiceName?.trim() || DEFAULT_SERVICE_NAME) as string
const appleClientId = (rawAppleClientId?.trim() || DEFAULT_APPLE_CLIENT_ID) as string

// ----- export -----
/** API 베이스 URL. 개발 시 Vite 프록시 → '/api', 프로덕션에서는 env 또는 기본값 */
export const API_BASE_URL = apiBaseUrl

/** Auth/Apple 콜백 등 공통 API용 호스트(origin). 경로 제외 */
export function getApiHost(): string {
  return apiHost
}

/** 백엔드에 보내는 서비스 명. Railway 등에서 VITE_SERVICE_NAME 설정 가능 */
export const SERVICE_NAME = serviceName

/**
 * 플래너 도메인 REST는 브라우저에서 이 세그먼트만 사용한다.
 * inside-api `PlannerDataApiPathRewriteFilter`가 서버 내부에서 기존 플래너 REST로 넘긴다.
 */
export const PLANNER_DATA_API_SEGMENT =
  (import.meta.env.VITE_PLANNER_DATA_API_SEGMENT as string | undefined)?.replace(/^\/+|\/+$/g, '').trim() ||
  'planner-data'

/** `apiClient` base가 `/api`일 때: `/planner-data/...` 형태의 경로 반환 */
export function plannerApiUrl(subpath: string): string {
  const p = subpath.replace(/^\/+/, '')
  return `/${PLANNER_DATA_API_SEGMENT}/${p}`
}

/** Sign in with Apple (웹) Client ID */
export const APPLE_WEB_CLIENT_ID = appleClientId

/** Apple 웹 콜백 URI. VITE_APPLE_CALLBACK_URI 미설정 시 getApiHost()/auth/apple/callback */
export function getAppleCallbackUri(): string {
  const custom = rawAppleCallbackUri?.trim()
  if (custom) return custom
  return `${apiHost}/auth/apple/callback`
}
